import React, {useMemo} from 'react';
import ContentArea from '@episerver/spa-core/Components/ContentArea';
import PageWrapper from 'app/Components/Shared/PageWrapper';
import Page from '@episerver/spa-core/Page';
import TheCounterPageData from 'app/Models/Content/TheCounterPageData';

import CategoryRefBlock from 'app/Models/Content/CategoryRefBlockData';
import DalpakItemBlock from 'app/Models/Content/DalpakItemBlockData';
import NewDalpakItemBlock from 'app/Models/Content/DalpakItemBlockData';
import GeneralInformationItemBlock from 'app/Models/Content/GeneralInformationItemBlockData';
import OperationBoxItemBlock from 'app/Models/Content/OperationBoxItemBlockData';

import minisearch from 'minisearch';
import SearchHintBuilder from './SearchHintBuilder';
import TheCounterSearch, {TheCounterSearchItem} from './TheCounterSearch';
import TheCounterCategories from './TheCounterCategories';
import IEpiserverContext from '@episerver/spa-core/Core/IEpiserverContext';
import {TheCounterProvider, Category} from './TheCounterContext';

type BlockSearchTypes = NewDalpakItemBlock | DalpakItemBlock | OperationBoxItemBlock | GeneralInformationItemBlock;
const BlockSearchTypeNames = ['NewDalpakItemsBlock', 'DalpakItemsBlock', 'OperationBoxItemsBlock', 'GeneralInformationItemsBlock'];

type TheCounterPageBlockProps = {
  data: TheCounterPageData;
  context: IEpiserverContext;
};

const TheCounterPageBlock = ({data, context}: TheCounterPageBlockProps) => {
  const {searchHintTitle, contentsCategories, mainContentArea} = data;
  const categories: Category[] = useMemo(
    () =>
      (contentsCategories.expandedValue as CategoryRefBlock[])?.map((c) => ({
        id: c.id.value,
        name: c.name.value,
        numberOfItemsPerRow: c.numberOfItemsPerRow.value,
      })),
    [contentsCategories]
  );

  const searchIndex = useMemo(() => {
    const index = new minisearch<TheCounterSearchItem>({
      idField: 'name',
      fields: ['hints'],
      storeFields: ['name', 'href'],
      searchOptions: {
        prefix: true,
        combineWith: 'AND',
      },
    });

    for (const block of mainContentArea.expandedValue) {
      if (BlockSearchTypeNames.every((t) => !block.contentType.includes(t))) continue;
      for (const item of ((block as any).operationBoxItems?.expandedValue as BlockSearchTypes[]) || []) {
        const name = typeof item.name === 'string' ? item.name : item.name.value;
        const searchHints = typeof item.searchHints === 'string' ? item.searchHints : item.searchHints.value;
        const hints = new SearchHintBuilder().add(name).add(searchHints).get();
        const href = item.link?.value ?? null;
        // ignore empty links
        if (href != null) index.add({name, hints, href});
      }
    }

    return index;
  }, [mainContentArea]);

  return (
    <TheCounterProvider>
      <TheCounterSearch index={searchIndex} placeholder={searchHintTitle.value} />
      <TheCounterCategories categories={categories} />
      <ContentArea context={context} data={mainContentArea} />
    </TheCounterProvider>
  );
};

export default class TheCounterPage extends Page<TheCounterPageData> {
  render() {
    return (
      <PageWrapper props={this.props}>
        <TheCounterPageBlock data={this.props.data} context={this.props.context} />;
      </PageWrapper>
    );
  }
}
